import { apiClient } from "./client";
import type { Assumption, CapacityBand, HubKpi, ValueKind } from "./types";

// Wire types and API client calls for the Shop Floor screens (release 0.4.0):
// 1. Dispatch (/shop-floor/dispatch)
// 2. Capacity & machines (/shop-floor/capacity)
// 3. Material & OSP + Launch board (/shop-floor/material)
// 4. Hotlist & holds (/shop-floor/hotlist)
// 5. Who's working (/shop-floor/working-now)

export interface ShopFloorEnvelope {
  generatedAtUtc: string;
  dataAsOfUtc: string | null;
  isStale: boolean | null;
  dataSource: string;
  dataFloorDate: string;
  plantDate: string;
  valueKind: ValueKind;
  kpis: HubKpi[];
  assumptions: Assumption[];
}

// ── 1. Dispatch ────────────────────────────────────────────────────────────────────────────────

export interface DispatchShopCell {
  shopCode: string;
  shopName: string;
  isVerified: boolean;
  workcenterCount: number;
  queuedOperationCount: number;
  readyOperationCount: number;
  blockedOperationCount: number;
  remainingHours: number;
  valueKind: ValueKind;
}

export interface DispatchWorkcenterSummary {
  workcenterCode: string;
  workcenterName: string;
  shopCode: string | null;
  shopName: string | null;
  machineCount: number;
  machineCountValueKind: ValueKind;
  queuedOperationCount: number;
  readyOperationCount: number;
  blockedOperationCount: number;
  sequenceViolationCount: number;
  remainingHours: number;
  weeklyCapacityHours: number | null;
  valueKind: ValueKind;
}

export interface DispatchOperation {
  rankPosition: number;
  operationKey: string;
  workOrderKey: string;
  job: string;
  suffix: string;
  sequence: number;
  workcenterCode: string;
  workcenterName: string;
  shopCode: string | null;
  partNumber: string;
  customerCode: string | null;
  description: string | null;
  quantityOrdered: number;
  quantityValueKind: ValueKind;
  priority: string;
  priorityValueKind: ValueKind;
  priorityReason: string | null;
  slackWorkdays: number | null;
  slackValueKind: ValueKind;
  dueDate: string | null;
  dueDateSource: string;
  dueDateValueKind: ValueKind;
  plannedStartDate: string | null;
  estimatedCompletionDate: string | null;
  ecdValueKind: ValueKind;
  ecdBasis: string;
  hoursEstimated: number;
  hoursActual: number;
  remainingHours: number;
  hoursValueKind: ValueKind;
  previousOpReady: boolean;
  previousOpNote: string | null;
  materialReady: boolean | null;
  materialStatus: string;
  ospClear: boolean;
  childrenReady: boolean;
  onHold: boolean;
  isReady: boolean;
  readinessState: string;
  hasSequenceViolation: boolean;
  sequenceViolationReason: string | null;
  blockers: string[];
}

export interface DispatchBoard extends ShopFloorEnvelope {
  selectedShop: string | null;
  selectedWorkcenter: string | null;
  shops: DispatchShopCell[];
  workcenters: DispatchWorkcenterSummary[];
  operations: DispatchOperation[];
}

export interface DispatchWorkcenter {
  generatedAtUtc: string;
  dataAsOfUtc: string | null;
  isStale: boolean | null;
  dataSource: string;
  dataFloorDate: string;
  plantDate: string;
  valueKind: ValueKind;
  workcenter: DispatchWorkcenterSummary;
  operations: DispatchOperation[];
  assumptions: Assumption[];
}

// ── 2. Capacity & machines ─────────────────────────────────────────────────────────────────────

export interface ShopFilterOption {
  code: string;
  name: string;
}

export interface WorkcenterFilterOption {
  code: string;
  name: string;
  shopCode: string | null;
}

export interface CapacityWeekCell {
  workcenterCode: string;
  weekStart: string;
  loadHours: number;
  operationCount: number;
  capacityHours: number | null;
  utilisationPercent: number | null;
  band: CapacityBand;
  valueKind: ValueKind;
}

export interface CapacityWorkcenterRow {
  workcenterCode: string;
  workcenterName: string;
  shopCode: string | null;
  shopName: string | null;
  machineCount: number;
  machineCountValueKind: ValueKind;
  hasCalendar: boolean;
  weeklyStandardHours: number | null;
  capacityValueKind: ValueKind;
  weeklyEffectiveHours: number | null;
  backlogHours: number;
  backlogOperationCount: number;
  backlogValueKind: ValueKind;
  queueDays: number;
  isBottleneck: boolean;
  weeks: CapacityWeekCell[];
}

export interface CapacityMachineRow {
  machineCode: string;
  machineName: string;
  workcenterCode: string;
  workcenterName: string;
  shopCode: string | null;
  assetTag: string | null;
  status: string;
  workcenterMachineCount: number;
  weeklyHoursPerMachine: number | null;
  valueKind: ValueKind;
  sourceNote: string;
}

export interface CapacityLoad extends ShopFloorEnvelope {
  horizonWeeks: number;
  selectedShop: string | null;
  bottleneckWorkcenter: string | null;
  bottleneckAverageUtilisationPercent: number | null;
  bottleneckQueueDays: number | null;
  weekStarts: string[];
  shops: ShopFilterOption[];
  workcenters: CapacityWorkcenterRow[];
  machines: CapacityMachineRow[];
}

export interface CapacityCellOperation {
  operationKey: string;
  workOrderKey: string;
  job: string;
  suffix: string;
  sequence: number;
  partNumber: string;
  customerCode: string | null;
  description: string | null;
  priority: string;
  plannedStartDate: string | null;
  estimatedCompletionDate: string | null;
  dueDate: string | null;
  hoursEstimated: number;
  hoursActual: number;
  remainingHours: number;
  valueKind: ValueKind;
  placementBasis: string;
}

export interface CapacityCellDetail {
  generatedAtUtc: string;
  dataAsOfUtc: string | null;
  isStale: boolean | null;
  dataSource: string;
  dataFloorDate: string;
  plantDate: string;
  workcenterCode: string;
  workcenterName: string;
  bucketLabel: string;
  weekStart: string | null;
  isBacklog: boolean;
  totalLoadHours: number;
  capacityHours: number | null;
  utilisationPercent: number | null;
  band: CapacityBand;
  valueKind: ValueKind;
  operations: CapacityCellOperation[];
}

// ── 3. Material & OSP + Launch board ───────────────────────────────────────────────────────────

export interface LaunchBoardJob {
  workOrderKey: string;
  job: string;
  suffix: string;
  partNumber: string;
  customerCode: string | null;
  salesOrder: string | null;
  keyLevel: string;
  priority: string;
  dueDate: string | null;
  dueDateSource: string;
  forecastFinishDate: string | null;
  materialReady: boolean | null;
  materialStatus: string;
  materialGateLabel: string;
  engineeringComplete: boolean;
  openEngineeringOpCount: number;
  engineeringGateLabel: string;
  ospClear: boolean;
  openOspOpCount: number;
  ospGateLabel: string;
  childrenReady: boolean | null;
  childCount: number;
  openChildCount: number;
  childrenGateLabel: string;
  readyToRun: boolean;
  overallVerdict: string;
  blockerCount: number;
  blockers: string[];
  valueKind: ValueKind;
}

export interface LaunchBoard extends ShopFloorEnvelope {
  totalJobsEvaluated: number;
  readyCount: number;
  blockedCount: number;
  jobs: LaunchBoardJob[];
}

export interface MaterialJobShortageRow {
  workOrderKey: string;
  job: string;
  suffix: string;
  parentPartNumber: string;
  customerCode: string | null;
  componentPart: string;
  consumingSequence: number;
  quantityCommitted: number;
  quantityIssued: number;
  outstandingQuantity: number;
  quantityOnHand: number;
  quantityOnOrder: number;
  flagShort: boolean;
  isShort: boolean;
  isWithinLookAhead: boolean;
  needByDate: string | null;
  jobMaterialStatus: string;
  partAvailability: string;
  openPurchaseOrder: string | null;
  vendorCode: string | null;
  purchaseOrderDueDate: string | null;
  reason: string;
  valueKind: ValueKind;
}

export interface MaterialPartSummary {
  workOrderKey: string;
  partNumber: string;
  customerCode: string | null;
  jobMaterialStatus: string;
  partAvailability: string;
  quantityRequired: number;
  quantityOnHand: number;
  quantityOnOrder: number;
  openMaterialLineCount: number;
  closedMaterialLineCount: number;
  nextOperationStartDate: string | null;
  nextOperationDescription: string | null;
  valueKind: ValueKind;
}

export interface MaterialPurchaseOrderLine {
  key: string;
  purchaseOrder: string;
  poLine: string;
  partNumber: string | null;
  description: string | null;
  workOrderKey: string | null;
  vendorCode: string | null;
  quantityOrdered: number;
  quantityReceived: number;
  quantityOpen: number;
  dateOrdered: string | null;
  dateDue: string | null;
  isOverdue: boolean;
  valueKind: ValueKind;
}

export interface MaterialShortages extends ShopFloorEnvelope {
  lookAheadDays: number;
  shortages: MaterialJobShortageRow[];
  partStatuses: MaterialPartSummary[];
  purchaseOrders: MaterialPurchaseOrderLine[];
}

export interface OspOperationRow {
  operationKey: string;
  workOrderKey: string;
  job: string;
  suffix: string;
  sequence: number;
  partNumber: string;
  customerCode: string | null;
  workcenterCode: string | null;
  description: string | null;
  purchaseOrder: string | null;
  poLine: string | null;
  poMatchSource: string;
  vendorCode: string | null;
  dateSentOrOpened: string | null;
  estimatedStartDate: string | null;
  dueDate: string | null;
  dueDateValueKind: ValueKind;
  estimatedCompletionDate: string | null;
  daysOut: number;
  daysOverdue: number;
  isOverdue: boolean;
  leadTimeDays: number;
  leadTimeValueKind: ValueKind;
  topLevelWorkOrderKey: string | null;
  treeImpact: string;
  valueKind: ValueKind;
}

export interface OspTracker extends ShopFloorEnvelope {
  openCount: number;
  overdueCount: number;
  missingPoCount: number;
  vendors: string[];
  operations: OspOperationRow[];
}

// ── 4. Hotlist & holds ─────────────────────────────────────────────────────────────────────────

export interface HotlistNote {
  id: number;
  hotlistEntryId: number;
  note: string;
  writtenBy: string;
  writtenAtUtc: string;
  valueKind: ValueKind;
}

export interface HotlistEntry {
  id: number;
  workOrderKey: string;
  job: string;
  suffix: string;
  partNumber: string;
  customerCode: string | null;
  description: string | null;
  priority: string;
  priorityValueKind: ValueKind;
  priorityReason: string | null;
  onHoldInGss: boolean;
  dueDate: string | null;
  dueDateSource: string;
  owner: string;
  reason: string;
  nextReviewOn: string | null;
  isReviewDue: boolean;
  isOpen: boolean;
  addedBy: string;
  addedAtUtc: string;
  ageWorkdays: number;
  ageValueKind: ValueKind;
  notes: HotlistNote[];
}

export interface HeldWorkOrder {
  workOrderKey: string;
  job: string;
  suffix: string;
  partNumber: string;
  customerCode: string | null;
  description: string | null;
  keyLevel: string;
  shopCode: string | null;
  dueDate: string | null;
  dueDateSource: string;
  ageWorkdays: number | null;
  ageBucket: string;
  ageValueKind: ValueKind;
  priority: string;
  isOnHotlist: boolean;
  nextOpenOperation: string | null;
  valueKind: ValueKind;
}

export interface HotlistAndHolds extends ShopFloorEnvelope {
  canEdit: boolean;
  hotPriorityCap: number;
  currentHotCount: number;
  hotlistEntries: HotlistEntry[];
  holdJobs: HeldWorkOrder[];
}

export interface AddHotlistEntryRequest {
  workOrderKey?: string | null;
  job?: string | null;
  suffix?: string | null;
  owner?: string | null;
  reason: string;
  nextReviewOn?: string | null;
  priority?: string | null;
  attestation?: string | null;
}

export interface RemoveHotlistEntryRequest {
  reason: string;
  attestation?: string | null;
}

export interface AddHotlistNoteRequest {
  note: string;
  attestation?: string | null;
}

// ── 5. Who's working ───────────────────────────────────────────────────────────────────────────

export interface WorkingNowEntry {
  personLabel: string;
  shift: string;
  shopCode: string | null;
  workcenterCode: string;
  workcenterName: string;
  workOrderKey: string;
  job: string;
  suffix: string;
  partNumber: string;
  customerCode: string | null;
  operationSequence: number;
  operationDescription: string | null;
  laborKind: string;
  workDate: string;
  clockInUtc: string | null;
  hoursOnJob: number;
  hoursValueKind: ValueKind;
  hoursBasis: string;
  operationEstimatedHours: number | null;
  operationRemainingHours: number | null;
  valueKind: ValueKind;
}

export interface WorkingNowWorkcenterGroup {
  workcenterCode: string;
  workcenterName: string;
  shopCode: string | null;
  shopName: string | null;
  activeOperatorCount: number;
  totalHoursOnJob: number;
  valueKind: ValueKind;
  entries: WorkingNowEntry[];
}

export interface WorkingNowBoard extends ShopFloorEnvelope {
  selectedShop: string | null;
  selectedWorkcenter: string | null;
  shops: ShopFilterOption[];
  workcenters: WorkcenterFilterOption[];
  byWorkcenter: WorkingNowWorkcenterGroup[];
  activeClockIns: WorkingNowEntry[];
  /** Every production machine with who is punched on it now, or idle (recorded; owner's ask 2026-10-23). */
  byMachine?: WorkingNowMachine[];
}

export interface WorkingNowMachine {
  workcenterCode: string;
  workcenterName: string;
  shopCode: string | null;
  state: "busy" | "idle";
  registryStatus: string | null;
  peopleCount: number;
  valueKind: ValueKind;
  people: WorkingNowEntry[];
}

// ── Query keys & API client functions ──────────────────────────────────────────────────────────

export const SHOPFLOOR_QK = {
  dispatch: (shop?: string, workcenter?: string) => ["shopfloor", "dispatch", shop ?? "", workcenter ?? ""] as const,
  capacity: (weeks = 8, shop?: string) => ["shopfloor", "capacity", weeks, shop ?? ""] as const,
  capacityCell: (wc: string, week: string) => ["shopfloor", "capacity-cell", wc, week] as const,
  launchBoard: (customer?: string, readyOnly = false) => ["shopfloor", "launch-board", customer ?? "", readyOnly] as const,
  materialShortages: (lookAheadDays = 14) => ["shopfloor", "material-shortages", lookAheadDays] as const,
  ospOpen: (vendor?: string, status?: string) => ["shopfloor", "osp-open", vendor ?? "", status ?? ""] as const,
  hotlist: ["shopfloor", "hotlist"] as const,
  workingNow: (shop?: string, workcenter?: string) => ["shopfloor", "working-now", shop ?? "", workcenter ?? ""] as const
} as const;

export const shopFloorApi = {
  getDispatchBoard: async (params?: { shop?: string; workcenter?: string }): Promise<DispatchBoard> =>
    (await apiClient.get<DispatchBoard>("/dispatch", { params })).data,

  getWorkcenterDispatch: async (workcenter: string): Promise<DispatchWorkcenter> =>
    (await apiClient.get<DispatchWorkcenter>(`/dispatch/${encodeURIComponent(workcenter)}`)).data,

  getCapacityLoad: async (params?: { weeks?: number; shop?: string; workcenter?: string }): Promise<CapacityLoad> =>
    (await apiClient.get<CapacityLoad>("/capacity/load", { params })).data,

  getCapacityCell: async (wc: string, week = "backlog"): Promise<CapacityCellDetail> =>
    (await apiClient.get<CapacityCellDetail>("/capacity/cell", { params: { wc, week } })).data,

  getLaunchBoard: async (params?: { customer?: string; readyOnly?: boolean }): Promise<LaunchBoard> =>
    (await apiClient.get<LaunchBoard>("/launch-board", { params })).data,

  // needBand (release 1.4.0 chart drill-down): only the short lines in one days-to-need band.
  getMaterialShortages: async (lookAheadDays = 14, needBand?: string): Promise<MaterialShortages> =>
    (await apiClient.get<MaterialShortages>("/material/shortages", { params: needBand ? { lookAheadDays, needBand } : { lookAheadDays } })).data,

  getOpenOsp: async (params?: { vendor?: string; status?: string; band?: string }): Promise<OspTracker> =>
    (await apiClient.get<OspTracker>("/osp/open", { params })).data,

  getHotlist: async (): Promise<HotlistAndHolds> =>
    (await apiClient.get<HotlistAndHolds>("/hotlist")).data,

  getHotlistNotes: async (id: number): Promise<HotlistNote[]> =>
    (await apiClient.get<HotlistNote[]>(`/hotlist/${id}/notes`)).data,

  addHotlistEntry: async (body: AddHotlistEntryRequest): Promise<HotlistEntry> =>
    (await apiClient.post<HotlistEntry>("/hotlist", body)).data,

  removeHotlistEntry: async (id: number, body: RemoveHotlistEntryRequest): Promise<HotlistEntry> =>
    (await apiClient.post<HotlistEntry>(`/hotlist/${id}/remove`, body)).data,

  addHotlistNote: async (id: number, body: AddHotlistNoteRequest): Promise<HotlistNote> =>
    (await apiClient.post<HotlistNote>(`/hotlist/${id}/notes`, body)).data,

  getWorkingNow: async (params?: { shop?: string; workcenter?: string }): Promise<WorkingNowBoard> =>
    (await apiClient.get<WorkingNowBoard>("/people/working-now", { params })).data
};
